import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { productService } from '../../services/products';
import { livestreamService } from '../../services/livestream';
import { orderService } from '../../services/orders';
import { paymentService } from '../../services/payments';
import { authService } from '../../services/auth';
import LoadingSpinner from '../../components/Common/LoadingSpinner';
import Alert from '../../components/Common/Alert';
import { ImageUtils } from '../../utils/imageUtils';

const Dashboard = ({ user, refreshUser }) => {
  const [dashboardData, setDashboardData] = useState({
    stats: {
      totalProducts: 0,
      totalLivestreams: 0,
      pendingOrders: 0,
      totalEarnings: 0
    },
    recentProducts: [],
    recentLivestreams: [],
    recentOrders: [],
    credits: 0,
    verificationStatus: 'pending_verification'
  });
  const [loading, setLoading] = useState(true);
  const [alert, setAlert] = useState(null);

  const loadDashboardData = useCallback(async () => {
    try {
      setLoading(true);

      const [
        productsData,
        livestreamsData,
        ordersData,
        earningsData,
        verificationData
      ] = await Promise.all([
        productService.getMyProducts().catch(() => ({ results: [] })),
        livestreamService.getMyLivestreams().catch(() => ({ results: [] })),
        orderService.getSellerOrders({ limit: 5 }).catch(() => ({ results: [] })),
        paymentService.getSellerEarnings().catch(() => ({ total_earnings: 0 })),
        authService.getVerificationStatus().catch(() => ({ verification_status: 'pending_verification', credits: 0 }))
      ]);

      setDashboardData({
        stats: {
          totalProducts: Array.isArray(productsData) ? productsData.length : (productsData.results?.length || 0),
          totalLivestreams: Array.isArray(livestreamsData) ? livestreamsData.length : (livestreamsData.results?.length || 0),
          pendingOrders: ordersData.results?.filter(order => order.status === 'pending').length || 0,
          totalEarnings: earningsData.total_earnings || 0
        },
        recentProducts: Array.isArray(productsData) ? productsData.slice(0, 5) : (productsData.results?.slice(0, 5) || []),
        recentLivestreams: Array.isArray(livestreamsData) ? livestreamsData.slice(0, 5) : (livestreamsData.results?.slice(0, 5) || []),
        recentOrders: ordersData.results || [],
        credits: verificationData.credits || user?.profile?.credits || 0,
        verificationStatus: verificationData.verification_status || user?.profile?.verification_status || 'pending_verification'
      });
    } catch (error) {
      console.error('Dashboard data loading error:', error);
      setAlert({
        type: 'danger',
        message: 'Failed to load dashboard data. Please refresh the page.'
      });
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    loadDashboardData();
  }, [loadDashboardData]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      completed: { color: '#10b981', bg: 'rgba(16, 185, 129, 0.1)', text: 'Completed' },
      pending: { color: '#f59e0b', bg: 'rgba(245, 158, 11, 0.1)', text: 'Pending' },
      processing: { color: '#3b82f6', bg: 'rgba(59, 130, 246, 0.1)', text: 'Processing' },
      cancelled: { color: '#ef4444', bg: 'rgba(239, 68, 68, 0.1)', text: 'Cancelled' },
      live: { color: '#ef4444', bg: 'rgba(239, 68, 68, 0.1)', text: 'Live' },
      scheduled: { color: '#f59e0b', bg: 'rgba(245, 158, 11, 0.1)', text: 'Scheduled' },
      ended: { color: '#64748b', bg: 'rgba(100, 116, 139, 0.1)', text: 'Ended' }
    };

    const config = statusConfig[status] || statusConfig.pending;

    return (
      <span className="px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider" style={{ backgroundColor: config.bg, color: config.color }}>
        {config.text}
      </span>
    );
  };

  const getVerificationBadge = (status) => {
    if (status === 'verified') {
      return (
        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-lg" style={{ backgroundColor: 'rgba(16, 185, 129, 0.1)', color: '#10b981' }}>
          <i className="bi bi-check-circle-fill"></i>
          <span className="text-xs font-bold uppercase tracking-wider">Verified</span>
        </div>
      );
    }
    return (
      <div className="inline-flex items-center gap-2 px-4 py-2 rounded-lg" style={{ backgroundColor: 'rgba(245, 158, 11, 0.1)', color: '#f59e0b' }}>
        <i className="bi bi-clock-fill"></i>
        <span className="text-xs font-bold uppercase tracking-wider">Pending</span>
      </div>
    );
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  const { stats, recentProducts, recentLivestreams, recentOrders, credits, verificationStatus } = dashboardData;

  return (
    <div className="min-h-screen p-4 sm:p-6" style={{ backgroundColor: '#0a0f1e' }}>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
          <div>
            <h2 className="text-2xl sm:text-3xl font-bold text-white tracking-tight mb-1">Seller Terminal</h2>
            <p className="text-[10px] sm:text-xs uppercase font-bold tracking-wider" style={{ color: '#64748b' }}>
              Welcome back, {user?.first_name || 'Seller'}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-3 sm:gap-4">
            {getVerificationBadge(verificationStatus)}
            <div className="flex items-center gap-2 px-3 sm:px-4 py-2 rounded-lg" style={{ backgroundColor: '#161e35' }}>
              <i className="bi bi-wallet2" style={{ color: '#ef4444' }}></i>
              <span className="text-sm sm:text-base text-white font-bold">{credits} Credits</span>
            </div>
          </div>
        </div>

        {alert && (
          <div className="mb-6">
            <Alert
              type={alert.type}
              message={alert.message}
              onClose={() => setAlert(null)}
            />
          </div>
        )}

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          {[
            { label: 'Total Products', value: stats.totalProducts, icon: 'bi-box-seam', color: '#3b82f6', link: '/products' },
            { label: 'Livestreams', value: stats.totalLivestreams, icon: 'bi-broadcast', color: '#ef4444', link: '/livestreams' },
            { label: 'Pending Orders', value: stats.pendingOrders, icon: 'bi-clock-history', color: '#f59e0b', link: '/orders' },
            { label: 'Total Earnings', value: formatCurrency(stats.totalEarnings), icon: 'bi-currency-rupee', color: '#10b981', link: '/payments' }
          ].map((stat, index) => (
            <Link key={index} to={stat.link} className="block">
              <div className="rounded-2xl p-6 relative overflow-hidden transition-all hover:scale-105" style={{ backgroundColor: '#161e35' }}>
                <div className="absolute top-0 right-0 p-4 opacity-10">
                  <i className={`bi ${stat.icon}`} style={{ fontSize: '60px', color: stat.color }}></i>
                </div>
                <div className="relative z-10">
                  <p className="text-xs uppercase font-bold tracking-wider mb-2" style={{ color: '#64748b' }}>{stat.label}</p>
                  <p className="text-3xl font-bold" style={{ color: stat.color }}>
                    {typeof stat.value === 'number' && stat.label !== 'Total Earnings' ? stat.value : stat.value}
                  </p>
                </div>
              </div>
            </Link>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          {/* Recent Products */}
          <div className="rounded-2xl overflow-hidden" style={{ backgroundColor: '#161e35' }}>
            <div className="p-6 flex items-center justify-between" style={{ borderBottom: '1px solid rgba(255, 255, 255, 0.05)' }}>
              <div className="flex items-center gap-3">
                <i className="bi bi-box-seam text-xl" style={{ color: '#3b82f6' }}></i>
                <h5 className="text-lg font-bold text-white">Recent Products</h5>
              </div>
              <Link to="/products" className="text-xs font-bold uppercase tracking-wider transition-all" style={{ color: '#ef4444' }}>
                View All <i className="bi bi-arrow-right ml-1"></i>
              </Link>
            </div>
            <div className="p-6">
              {recentProducts.length > 0 ? (
                <div className="space-y-3">
                  {recentProducts.map((product) => (
                    <Link key={product.id} to={`/products/${product.id}/edit`} className="block">
                      <div className="flex items-center gap-4 p-4 rounded-lg transition-all hover:bg-opacity-50" style={{ backgroundColor: '#0f1420' }}>
                        <img
                          src={ImageUtils.getProductImageUrl(product)}
                          alt={product.name}
                          className="w-12 h-12 rounded-lg object-cover"
                          style={{ backgroundColor: '#161e35' }}
                          onError={(e) => { e.target.src = ImageUtils.getPlaceholderImage(); }}
                        />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-bold text-white truncate">{product.name}</p>
                          <p className="text-xs" style={{ color: '#64748b' }}>{formatCurrency(product.base_price || product.price)}</p>
                        </div>
                        <i className="bi bi-chevron-right" style={{ color: '#64748b' }}></i>
                      </div>
                    </Link>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <i className="bi bi-inbox text-4xl mb-2" style={{ color: '#64748b' }}></i>
                  <p className="text-sm" style={{ color: '#64748b' }}>No products yet</p>
                  <Link to="/products/new" className="inline-block mt-4 px-4 py-2 rounded-lg text-xs font-bold uppercase tracking-wider transition-all" style={{ backgroundColor: '#ef4444', color: '#fff' }}>
                    Add Product
                  </Link>
                </div>
              )}
            </div>
          </div>

          {/* Recent Livestreams */}
          <div className="rounded-2xl overflow-hidden" style={{ backgroundColor: '#161e35' }}>
            <div className="p-6 flex items-center justify-between" style={{ borderBottom: '1px solid rgba(255, 255, 255, 0.05)' }}>
              <div className="flex items-center gap-3">
                <i className="bi bi-broadcast text-xl" style={{ color: '#ef4444' }}></i>
                <h5 className="text-lg font-bold text-white">Recent Livestreams</h5>
              </div>
              <Link to="/livestreams" className="text-xs font-bold uppercase tracking-wider transition-all" style={{ color: '#ef4444' }}>
                View All <i className="bi bi-arrow-right ml-1"></i>
              </Link>
            </div>
            <div className="p-6">
              {recentLivestreams.length > 0 ? (
                <div className="space-y-3">
                  {recentLivestreams.map((livestream) => (
                    <Link key={livestream.id} to={`/livestreams/${livestream.id}`} className="block">
                      <div className="flex items-center gap-4 p-4 rounded-lg transition-all hover:bg-opacity-50" style={{ backgroundColor: '#0f1420' }}>
                        <div className="w-12 h-12 rounded-lg flex items-center justify-center" style={{ backgroundColor: 'rgba(239, 68, 68, 0.1)' }}>
                          <i className="bi bi-play-circle text-2xl" style={{ color: '#ef4444' }}></i>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-bold text-white truncate">{livestream.title}</p>
                          <p className="text-xs" style={{ color: '#64748b' }}>{formatDate(livestream.scheduled_at || livestream.created_at)}</p>
                        </div>
                        {getStatusBadge(livestream.status)}
                      </div>
                    </Link>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <i className="bi bi-inbox text-4xl mb-2" style={{ color: '#64748b' }}></i>
                  <p className="text-sm" style={{ color: '#64748b' }}>No livestreams yet</p>
                  <Link to="/livestreams/new" className="inline-block mt-4 px-4 py-2 rounded-lg text-xs font-bold uppercase tracking-wider transition-all" style={{ backgroundColor: '#ef4444', color: '#fff' }}>
                    Create Livestream
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Recent Orders */}
        <div className="rounded-2xl overflow-hidden" style={{ backgroundColor: '#161e35' }}>
          <div className="p-6 flex items-center justify-between" style={{ borderBottom: '1px solid rgba(255, 255, 255, 0.05)' }}>
            <div className="flex items-center gap-3">
              <i className="bi bi-receipt text-xl" style={{ color: '#f59e0b' }}></i>
              <h5 className="text-lg font-bold text-white">Recent Orders</h5>
            </div>
            <Link to="/orders" className="text-xs font-bold uppercase tracking-wider transition-all" style={{ color: '#ef4444' }}>
              View All <i className="bi bi-arrow-right ml-1"></i>
            </Link>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr style={{ borderBottom: '1px solid rgba(255, 255, 255, 0.05)' }}>
                  <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider" style={{ color: '#64748b' }}>Order ID</th>
                  <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider" style={{ color: '#64748b' }}>Customer</th>
                  <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider" style={{ color: '#64748b' }}>Amount</th>
                  <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider" style={{ color: '#64748b' }}>Status</th>
                  <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider" style={{ color: '#64748b' }}>Date</th>
                </tr>
              </thead>
              <tbody>
                {recentOrders.length > 0 ? (
                  recentOrders.map((order) => (
                    <tr key={order.id} style={{ borderBottom: '1px solid rgba(255, 255, 255, 0.05)' }}>
                      <td className="px-6 py-4">
                        <span className="text-sm font-mono text-white">#{order.id}</span>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-sm text-white">{order.buyer_name || 'N/A'}</span>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-sm font-bold" style={{ color: '#10b981' }}>{formatCurrency(order.total_amount)}</span>
                      </td>
                      <td className="px-6 py-4">
                        {getStatusBadge(order.status)}
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-sm" style={{ color: '#64748b' }}>{formatDate(order.created_at)}</span>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="5" className="px-6 py-12 text-center">
                      <div className="flex flex-col items-center gap-3">
                        <i className="bi bi-inbox text-4xl" style={{ color: '#64748b' }}></i>
                        <p className="text-sm" style={{ color: '#64748b' }}>No orders yet</p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
          {[
            { label: 'Add Product', icon: 'bi-plus-circle', link: '/products/new', color: '#3b82f6' },
            { label: 'Create Livestream', icon: 'bi-broadcast', link: '/livestreams/new', color: '#ef4444' },
            { label: 'View Orders', icon: 'bi-receipt', link: '/orders', color: '#f59e0b' },
            { label: 'Manage Credits', icon: 'bi-wallet2', link: '/credits', color: '#10b981' }
          ].map((action, index) => (
            <Link key={index} to={action.link}>
              <div className="p-4 rounded-xl text-center transition-all hover:scale-105" style={{ backgroundColor: '#161e35' }}>
                <i className={`bi ${action.icon} text-3xl mb-2`} style={{ color: action.color }}></i>
                <p className="text-xs font-bold uppercase tracking-wider text-white">{action.label}</p>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
